(() => {
  'use strict';

  const STORAGE_KEY = 'tomauto_cookie_choice_v2';
  const MAX_API_ATTEMPTS = 30;
  const API_RETRY_DELAY = 150;

  const banner = document.getElementById('tm-cookie-banner');
  const modal = document.getElementById('tm-cookie-modal');

  if (!banner || !modal) return;

  const prefInput = document.getElementById('tm-consent-preferences');
  const analyticsInput = document.getElementById('tm-consent-analytics');
  const marketingInput = document.getElementById('tm-consent-marketing');

  const acceptButton = banner.querySelector('[data-tmcb-accept]');
  const rejectButton = banner.querySelector('[data-tmcb-reject]');
  const customizeButton = banner.querySelector('[data-tmcb-customize]');
  const modalSaveButton = modal.querySelector('[data-tmcm-save]');
  const modalRejectButton = modal.querySelector('[data-tmcm-reject]');
  const modalCloseButtons = modal.querySelectorAll('[data-tmcm-close]');

  let retryTimer = 0;
  let apiLoading = false;
  let pendingChoice = null;
  let previouslyFocused = null;

  const isBoolean = (value) => typeof value === 'boolean';

  function normalizeChoice(value) {
    if (!value || typeof value !== 'object') return null;

    if (
      !isBoolean(value.preferences) ||
      !isBoolean(value.analytics) ||
      !isBoolean(value.marketing)
    ) {
      return null;
    }

    return {
      preferences: value.preferences,
      analytics: value.analytics,
      marketing: value.marketing
    };
  }

  function readSavedChoice() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const choice = normalizeChoice(JSON.parse(raw));
      if (choice) return choice;

      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (_) {}
    }

    return null;
  }

  function saveChoice(choice) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    } catch (_) {}
  }

  function setInputs(choice) {
    const safeChoice = choice || {
      preferences: false,
      analytics: false,
      marketing: false
    };

    prefInput.checked = safeChoice.preferences;
    analyticsInput.checked = safeChoice.analytics;
    marketingInput.checked = safeChoice.marketing;
  }

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.offsetParent !== null);
  }

  function trapFocus(event, container) {
    if (event.key !== 'Tab') return;

    const focusable = getFocusable(container);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function showBanner() {
    banner.hidden = false;
  }

  function hideBanner() {
    banner.hidden = true;
  }

  function openModal() {
    const savedChoice = readSavedChoice();
    setInputs(savedChoice);

    previouslyFocused = document.activeElement;
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';

    const closeButton = modal.querySelector('.tmcm__close');
    if (closeButton) closeButton.focus();
  }

  function closeModal() {
    modal.hidden = true;
    document.documentElement.style.overflow = '';

    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }

    previouslyFocused = null;
  }

  function sendConsent(choice) {
    const customerPrivacy =
      window.Shopify &&
      window.Shopify.customerPrivacy;

    if (
      !customerPrivacy ||
      typeof customerPrivacy.setTrackingConsent !== 'function'
    ) {
      return false;
    }

    try {
      customerPrivacy.setTrackingConsent({
        preferences: choice.preferences,
        analytics: choice.analytics,
        marketing: choice.marketing,
        sale_of_data: false
      });

      pendingChoice = null;
      return true;
    } catch (_) {
      return false;
    }
  }

  function scheduleRetry(attempt) {
    if (retryTimer || attempt >= MAX_API_ATTEMPTS) return;

    retryTimer = window.setTimeout(() => {
      retryTimer = 0;
      ensureConsentApi(attempt + 1);
    }, API_RETRY_DELAY);
  }

  function ensureConsentApi(attempt = 0) {
    if (!pendingChoice) return;

    if (sendConsent(pendingChoice)) return;

    if (
      window.Shopify &&
      typeof window.Shopify.loadFeatures === 'function' &&
      !apiLoading
    ) {
      apiLoading = true;

      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            apiLoading = false;

            if (!error && pendingChoice && sendConsent(pendingChoice)) {
              return;
            }

            scheduleRetry(attempt);
          }
        );

        return;
      } catch (_) {
        apiLoading = false;
      }
    }

    scheduleRetry(attempt);
  }

  function applyChoice(choice) {
    const normalized = normalizeChoice(choice);
    if (!normalized) return;

    saveChoice(normalized);
    setInputs(normalized);
    pendingChoice = normalized;

    hideBanner();
    closeModal();
    ensureConsentApi();
  }

  function acceptAll() {
    applyChoice({
      preferences: true,
      analytics: true,
      marketing: true
    });
  }

  function rejectAll() {
    applyChoice({
      preferences: false,
      analytics: false,
      marketing: false
    });
  }

  function savePreferences() {
    applyChoice({
      preferences: prefInput.checked,
      analytics: analyticsInput.checked,
      marketing: marketingInput.checked
    });
  }

  function init() {
    const savedChoice = readSavedChoice();

    if (savedChoice) {
      setInputs(savedChoice);
      pendingChoice = savedChoice;
      ensureConsentApi();
    } else {
      setInputs(null);
      showBanner();
    }
  }

  acceptButton?.addEventListener('click', acceptAll);
  rejectButton?.addEventListener('click', rejectAll);
  customizeButton?.addEventListener('click', openModal);

  modalSaveButton?.addEventListener('click', savePreferences);
  modalRejectButton?.addEventListener('click', rejectAll);

  modalCloseButtons.forEach((element) => {
    element.addEventListener('click', closeModal);
  });

  modal.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
      return;
    }

    trapFocus(event, modal);
  });

  banner.addEventListener('keydown', (event) => {
    trapFocus(event, banner);
  });

  window.tomautoOpenCookiePreferences = openModal;

  init();
})();
